import React, { useEffect, useState, useRef, useMemo } from "react";
import "../styles/Rooms.css";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { useBookingCart, MAX_GROUP_ROOMS } from "../context/BookingCartContext";
import Toast from "../components/Toast";
import { t } from "../i18n";
import API from "../config";

/* ไอคอนรถเข็น — ใช้บนปุ่มเพิ่มลงตะกร้าของการ์ดห้อง
   วาดเป็น svg inline ตามแบบไอคอนอื่นในหน้านี้ (ไม่พึ่ง icon library) */
function CartIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="9" cy="20" r="1.4" />
      <circle cx="18" cy="20" r="1.4" />
      <path d="M2 3h2.2l2.4 12.1a1.6 1.6 0 0 0 1.6 1.3h8.9a1.6 1.6 0 0 0 1.6-1.3L22 7H5" />
    </svg>
  );
}

/* กากบาท — ปุ่มปิด drawer ตะกร้า */
function CloseIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

/* ถังขยะ — ใช้แทนปุ่ม − ตอนเหลือห้องเดียว (กดแล้วเอาออกจากตะกร้าเลย) */
function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />
    </svg>
  );
}

/* ============================================================
   GUEST PICKER
   จำนวนห้องย้ายไปอยู่ที่ตะกร้าแล้ว (เลือกได้หลายประเภท) — ที่นี่เหลือแค่จำนวนคน
   ============================================================ */
function GuestPicker({ adults, setAdults, onClose, lang }) {
  const [localAdults, setLocalAdults] = useState(adults);

  const handleConfirm = () => {
    setAdults(localAdults);
    onClose();
  };

  return (
    <div className="guest-picker" onClick={e => e.stopPropagation()}>
      <div className="guest-row">
        <div><div className="guest-label">{t(lang,"adults")}</div><div className="guest-sub">{t(lang,"adultsAge")}</div></div>
        <div className="guest-counter">
          <button onClick={e => { e.stopPropagation(); setLocalAdults(Math.max(1, localAdults - 1)); }}>−</button>
          <span>{localAdults}</span>
          <button onClick={e => { e.stopPropagation(); setLocalAdults(Math.min(10, localAdults + 1)); }}>+</button>
        </div>
      </div>
      <button className="guest-done" onClick={handleConfirm}>{t(lang,"confirm")}</button>
    </div>
  );
}

/* ============================================================
   ROOMS PAGE
   Navbar ไม่อยู่ในนี้ — จัดการโดย App.jsx / Layout ที่ครอบอยู่:
     /rooms          → <Navbar>        (public)
     /booking        → StaffLayout     (NavbarStaff)
     /admin/booking  → <NavbarAdmin>   (admin)
   ============================================================ */
function Rooms() {
  const [rooms,      setRooms]      = useState([]);
  const navigate = useNavigate();
  const { lang } = useLanguage();

  const fmt   = (d) => d.toISOString().split("T")[0];
  const today = new Date();          // ใช้เป็น min ของ input วันที่ — ห้ามเลือกย้อนหลัง

  /* วันที่ + จำนวนคน + ตะกร้า อยู่ใน context เพื่อให้รอดตอนเข้าหน้า RoomDetail แล้วกด Back */
  const {
    checkIn, setCheckIn, nights, setNights, adults, setAdults,
    items, totalRooms, qtyOf, setQty, removeItem, clear, reconcile,
  } = useBookingCart();

  const [showGuest,  setShowGuest]  = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [showCart,   setShowCart]   = useState(false);
  const [typeFilter, setTypeFilter] = useState("All");
  const [priceRange, setPriceRange] = useState([0, 999999]);
  const [sortOrder,  setSortOrder]  = useState("none");

  /* ห้องว่างต่อประเภทในช่วงวันที่เลือก — { [roomTypeId]: {total, free} }
     showCounts มาจาก server (staff/admin เห็นตัวเลข ลูกค้าเห็นแค่ "เต็ม") */
  const [avail,        setAvail]        = useState({});
  const [showCounts,   setShowCounts]   = useState(false);
  const [availLoading, setAvailLoading] = useState(true);

  const [toasting, setToasting] = useState(null);
  const toast = (msg, type = "error") => {
    setToasting({ msg, type });
    setTimeout(() => setToasting(null), 3000);
  };

  const guestRef  = useRef(null);
  const filterRef = useRef(null);

  const checkOut = (() => {
    const d = new Date(checkIn);
    d.setDate(d.getDate() + nights);
    return fmt(d);
  })();

  useEffect(() => {
    fetch(`${API}/api/rooms`)
      .then(r => r.json())
      .then(d => {
        const list = Array.isArray(d) ? d : [];
        setRooms(list);
        const prices = list.map(r => r.price).filter(Boolean);
        if (prices.length) {
          setPriceRange([Math.min(...prices), Math.max(...prices)]);
        }
      });
  }, []);

  /* ห้องว่างของวันที่เลือก
     debounce — stepper จำนวนคืนกดรัวได้ 1→5 ไม่งั้นยิง 5 request
     AbortController — response ของวันเก่ามาช้าแล้วทับของใหม่ จะทำให้เพิ่มห้องที่ไม่ว่างได้ */
  useEffect(() => {
    if (!checkIn || !checkOut) return;
    const ctrl = new AbortController();
    setAvailLoading(true);

    const timer = setTimeout(() => {
      const token = localStorage.getItem("token");
      fetch(`${API}/api/rooms/availability?checkIn=${checkIn}&checkOut=${checkOut}`, {
        signal: ctrl.signal,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
        .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
        .then(d => {
          const map = d.availability || {};
          setAvail(map);
          setShowCounts(!!d.showCounts);
          setAvailLoading(false);
          // วันที่เปลี่ยนแล้วห้องว่างน้อยลง — หั่นจำนวนในตะกร้าลงมา ไม่ล้างทั้งตะกร้า
          if (reconcile(map)) toast(t(lang, "cartTrimmed"), "warning");
        })
        .catch(err => {
          if (err.name === "AbortError") return;
          console.error("[availability]", err);
          setAvailLoading(false);
          toast(t(lang, "availError"));
        });
    }, 250);

    return () => { clearTimeout(timer); ctrl.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkIn, checkOut]);

  /* ตะกร้าไม่อยู่ใน handler นี้ — ปุ่มเปิดตะกร้าอยู่ "นอก" drawer ถ้าเอามารวม
     mousedown จะสั่งปิดก่อน click จะสั่งเปิด กลายเป็นกดแล้วไม่ติด ใช้คลิก overlay ปิดแทน */
  useEffect(() => {
    const h = (e) => {
      if (guestRef.current  && !guestRef.current.contains(e.target))  setShowGuest(false);
      if (filterRef.current && !filterRef.current.contains(e.target)) setShowFilter(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  /* drawer เปิดอยู่: กด Esc ปิดได้ + ล็อกสกรอลล์หน้าหลัง ไม่งั้นเลื่อนใน drawer จนสุดแล้วหน้าหลังเลื่อนตาม */
  useEffect(() => {
    if (!showCart) return;
    const onKey = (e) => { if (e.key === "Escape") setShowCart(false); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [showCart]);

  const fmtDisplay = (s) => new Date(s).toLocaleDateString("th-TH", { day: "numeric", month: "short", year: "numeric" });
  const dayName    = (s) => new Date(s).toLocaleDateString("en-US", { weekday: "long" });

  const typeOptions = ["All", ...new Set(rooms.map(r => r.type).filter(Boolean))];
  const allPrices   = rooms.map(r => r.price).filter(Boolean);
  const minPrice    = allPrices.length ? Math.min(...allPrices) : 0;
  const maxPrice    = allPrices.length ? Math.max(...allPrices) : 999999;

  const filteredRooms = rooms
    .filter(r => {
      const adultOk = (r.adults || 1) >= adults;
      const typeOk  = typeFilter === "All" || r.type === typeFilter;
      const rateOk  = r.price >= priceRange[0] && r.price <= priceRange[1];
      return adultOk && typeOk && rateOk;
    })
    .sort((a, b) => {
      if (sortOrder === "asc")  return a.price - b.price;
      if (sortOrder === "desc") return b.price - a.price;
      return 0;
    });

  const priceChanged = priceRange[0] !== minPrice || priceRange[1] !== maxPrice;
  const hasFilter    = typeFilter !== "All" || priceChanged || sortOrder !== "none";
  const filterLabel  = hasFilter
    ? [
        typeFilter !== "All" && typeFilter,
        priceChanged && `${priceRange[0].toLocaleString()}–${priceRange[1].toLocaleString()} THB`,
        sortOrder === "asc"  && t(lang, "priceUp"),
        sortOrder === "desc" && t(lang, "priceDown"),
      ].filter(Boolean).join(" · ")
    : t(lang, "filterLabel");

  /* ── ตะกร้า ──
     lookup จาก rooms ทั้งหมด ไม่ใช่ filteredRooms — ตัวกรอง (คน/ประเภท/ราคา) ซ่อนการ์ดได้
     ถ้าแผงตะกร้าวนจาก filteredRooms รายการที่ถูกซ่อนจะค้างลบไม่ได้ */
  const roomById = useMemo(
    () => Object.fromEntries(rooms.map(r => [r._id, r])),
    [rooms],
  );

  const freeOf = (id) => avail[id]?.free;

  /* เพดานของประเภทนี้ = min(ห้องว่างจริง, โควต้าที่เหลือของทั้งตะกร้า) */
  const capOf = (id) => {
    const free = freeOf(id);
    return Math.min(
      Number.isFinite(free) ? free : MAX_GROUP_ROOMS,
      MAX_GROUP_ROOMS - totalRooms + qtyOf(id),
    );
  };

  const cartRows = items
    .map(it => ({ ...it, room: roomById[it.roomTypeId] }))
    .filter(r => r.room);

  const cartTotal = cartRows.reduce((s, r) => s + (r.room.price || 0) * r.qty * nights, 0);

  const bump = (e, id, next) => {
    e.stopPropagation();                       // ปุ่มอยู่ในการ์ดที่คลิกแล้วเด้งไปหน้า detail
    const cap = capOf(id);
    if (next > cap) {
      toast(next > qtyOf(id) && cap === MAX_GROUP_ROOMS - totalRooms + qtyOf(id)
        ? t(lang, "cartMaxReached", MAX_GROUP_ROOMS)
        : t(lang, "cartSoldOutMsg"), "warning");
      return;
    }
    setQty(id, next, freeOf(id));
  };

  const goToDetail = (id) =>
    navigate(`/roomdetail/${id}`, {
      state: { checkIn, checkOut, nights, adults, free: freeOf(id), showCounts },
    });

  const goToCheckout = () => {
    if (!cartRows.length) return;
    navigate("/payment", {
      state: {
        items: cartRows.map(r => ({ room: r.room, qty: r.qty })),
        checkIn, checkOut, nights,
        totalPrice: cartTotal,
        persons: adults,
      },
    });
  };

  return (
    <div className="rooms-page">
    <div className="rooms-inner">

      {/* ── STICKY SEARCH BAR ── */}
      <div className="search-sticky-wrap">
        <div className="search-glass">
          <div className="search-fields-row">

            {/* Check-in */}
            <div className="sg-field" onClick={() => document.getElementById("ci").showPicker?.()}>
              <div className="sg-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </div>
              <div className="sg-text">
                <span className="sg-label">{t(lang,"checkInLabel").toUpperCase()}</span>
                <span className="sg-val">{fmtDisplay(checkIn)}</span>
                <span className="sg-sub">{dayName(checkIn)}</span>
              </div>
              <input id="ci" type="date" className="sf-hidden-input"
                value={checkIn} min={fmt(today)}
                onChange={e => setCheckIn(e.target.value)}
              />
            </div>

            <div className="sg-divider" />

            {/* Nights */}
            <div className="sg-field sg-field-nights">
              <div className="sg-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                </svg>
              </div>
              <div className="sg-text">
                <span className="sg-label">{t(lang,"nightsLabel")}</span>
                <span className="sg-val">{nights} {t(lang,"nightsUnit")}</span>
                <span className="sg-sub">{t(lang,"checkoutLabel")}: {fmtDisplay(checkOut)}</span>
              </div>
              <div className="sg-nights-btns">
                <button onClick={e => { e.stopPropagation(); setNights(Math.max(1, nights - 1)); }}>−</button>
                <button onClick={e => { e.stopPropagation(); setNights(Math.min(30, nights + 1)); }}>+</button>
              </div>
            </div>

            <div className="sg-divider" />

            {/* Guests */}
            <div className="sg-field" ref={guestRef} onClick={() => setShowGuest(v => !v)}>
              <div className="sg-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <div className="sg-text">
                <span className="sg-label">{t(lang,"guestsLabel")}</span>
                <span className="sg-val">{adults} {t(lang,"adults")}</span>
                <span className="sg-sub">{nights} {t(lang,"nightsUnit")}</span>
              </div>
              <svg className={`sg-chevron ${showGuest ? "up" : ""}`} width="13" height="13"
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
              {showGuest && (
                <GuestPicker adults={adults} setAdults={setAdults}
                  onClose={() => setShowGuest(false)} lang={lang} />
              )}
            </div>

            <div className="sg-divider" />

            {/* Filter */}
            <div
              className={`sg-field sg-field-filter ${hasFilter ? "sg-filter-active" : ""}`}
              ref={filterRef}
              onClick={() => setShowFilter(v => !v)}
            >
              <div className="sg-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="4" y1="6" x2="20" y2="6"/>
                  <line x1="8" y1="12" x2="16" y2="12"/>
                  <line x1="11" y1="18" x2="13" y2="18"/>
                </svg>
              </div>
              <div className="sg-text">
                <span className="sg-label">{t(lang,"filterLabel")}</span>
                <span className="sg-val">{filterLabel}</span>
              </div>
              <svg className={`sg-chevron ${showFilter ? "up" : ""}`} width="13" height="13"
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="6 9 12 15 18 9"/>
              </svg>

              {showFilter && (
                <div className="filter-panel" onClick={e => e.stopPropagation()}>

                  <div className="fp-section">
                    <div className="fp-title">{t(lang,"roomType")}</div>
                    <div className="fp-options">
                      {typeOptions.map(opt => (
                        <label key={opt} className={`fp-item ${typeFilter === opt ? "selected" : ""}`}
                          onClick={() => setTypeFilter(opt)}>
                          <span className="fp-radio" />
                          {opt === "All" ? t(lang,"allTypes") : opt}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="fp-divider" />

                  <div className="fp-section">
                    <div className="fp-title">{t(lang,"roomRate")}</div>
                    <div className="fp-price-display">
                      <span>{priceRange[0].toLocaleString()} THB</span>
                      <span>–</span>
                      <span>{priceRange[1].toLocaleString()} THB</span>
                    </div>
                    <div className="fp-range-wrap">
                      <div className="fp-range-track">
                        <div
                          className="fp-range-fill"
                          style={{
                            left: `${((priceRange[0] - minPrice) / (maxPrice - minPrice || 1)) * 100}%`,
                            right: `${100 - ((priceRange[1] - minPrice) / (maxPrice - minPrice || 1)) * 100}%`,
                          }}
                        />
                      </div>
                      <input type="range" className="fp-range fp-range-min"
                        min={minPrice} max={maxPrice} step={100}
                        value={priceRange[0]}
                        onChange={e => {
                          const v = Number(e.target.value);
                          if (v <= priceRange[1]) setPriceRange([v, priceRange[1]]);
                        }}
                      />
                      <input type="range" className="fp-range fp-range-max"
                        min={minPrice} max={maxPrice} step={100}
                        value={priceRange[1]}
                        onChange={e => {
                          const v = Number(e.target.value);
                          if (v >= priceRange[0]) setPriceRange([priceRange[0], v]);
                        }}
                      />
                    </div>
                    <div className="fp-range-labels">
                      <span>{minPrice.toLocaleString()}</span>
                      <span>{maxPrice.toLocaleString()}</span>
                    </div>
                  </div>

                  <div className="fp-divider" />

                  <div className="fp-section">
                    <div className="fp-title">{t(lang,"sortByPrice")}</div>
                    <div className="fp-options">
                      {[
                        { value: "none", labelKey: "sortDefault" },
                        { value: "asc",  labelKey: "sortAsc" },
                        { value: "desc", labelKey: "sortDesc" },
                      ].map(opt => (
                        <label
                          key={opt.value}
                          className={`fp-item ${sortOrder === opt.value ? "selected" : ""}`}
                          onClick={() => setSortOrder(opt.value)}
                        >
                          <span className="fp-radio" />
                          {t(lang, opt.labelKey)}
                        </label>
                      ))}
                    </div>
                  </div>

                  {hasFilter && (
                    <button className="fp-clear" onClick={() => {
                      setTypeFilter("All");
                      setPriceRange([minPrice, maxPrice]);
                      setSortOrder("none");
                    }}>
                      {t(lang,"filterClear")}
                    </button>
                  )}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* Result count */}
      <div className="rooms-result-info">
        <span>{t(lang,"resultInfo",filteredRooms.length,adults,nights)}</span>
      </div>

      {/* ── ROOM LIST ── */}
      <div className="rooms-list">
        {filteredRooms.length === 0 && (
          <div className="rooms-empty"><span>😔</span><p>{t(lang,"noRooms")}</p></div>
        )}
        {filteredRooms.map(room => {
          const qty  = qtyOf(room._id);
          const free = freeOf(room._id);
          const full = free === 0;
          const cap  = capOf(room._id);
          return (
          <div className={`room-card ${qty > 0 ? "rc-in-cart" : ""}`} key={room._id}
               onClick={() => goToDetail(room._id)}>
            <div className="room-card-img">
              {/* ห้องที่ยังไม่ได้อัปรูปจะได้ src="/uploads/" แล้วขึ้นไอคอนรูปเสีย — ใส่ placeholder แทน */}
              {room.image
                ? <img src={`/uploads/${room.image}`} alt={lang === "en" && room.nameEn ? room.nameEn : room.name} />
                : <div className="room-card-img-ph">🛏️</div>}
            </div>
            <div className="room-card-body">
              <div className="room-card-info">
                <h3>{lang === "en" && room.nameEn ? room.nameEn : room.name}</h3>
                <div className="room-tags">
                  {room.breakfast && <span className="tag">🍳 {t(lang,"breakfast")}</span>}
                  <span className="tag">👥 {t(lang,"capacity",room.adults,room.children||0)}</span>
                  {room.type && <span className="tag">🏠 {room.type}</span>}
                </div>
                {room.description && <p className="room-desc">{room.description}</p>}
              </div>
              {/* ลำดับการอ่าน: สถานะห้องว่าง → ราคา → ปุ่มลงมือทำ
                  ป้ายห้องว่างต้องอยู่ติดราคา ไม่ใช่แทรกกลางระหว่างปุ่มสองอัน
                  ห้องเต็มยังอยู่ในลิสต์ ติดป้ายไว้ — ซ่อนไปจะเสียสัญญาณว่า "มีประเภทนี้แต่เต็ม"
                  ตัวเลขที่เหลือโชว์เฉพาะ staff/admin (showCounts มาจาก server) */}
              <div className="room-card-price">
                {full
                  ? <span className="rc-avail rc-avail-full">{t(lang,"soldOut")}</span>
                  : showCounts && Number.isFinite(free)
                    ? <span className={`rc-avail ${free <= 3 ? "rc-avail-low" : ""}`}>{t(lang,"roomsLeft",free)}</span>
                    : null}

                <div className="price-per-night">{room.price?.toLocaleString()} <span>{t(lang,"perNight")}</span></div>
                <div className="price-total">{t(lang,"totalNights",nights,(room.price*nights)?.toLocaleString())}</div>

                <div className="rc-actions" onClick={e => e.stopPropagation()}>
                  {/* ปุ่มนี้ไม่ผูก onClick เอง — อาศัยคลิกทั้งการ์ด แต่ rc-actions กัน propagation ไว้
                      เลยต้องเรียก goToDetail ตรงนี้ */}
                  <button type="button" className="price-cta"
                    onClick={() => goToDetail(room._id)}>{t(lang,"viewDetail")}</button>

                  {qty > 0 ? (
                    <div className="rc-qty">
                      {/* ที่จำนวน 1 ปุ่มลบกลายเป็นถังขยะ — ไม่ต้องมีปุ่ม × แยกอีกอัน เปลืองที่ */}
                      <button type="button" className="rc-qty-btn"
                        title={qty === 1 ? t(lang,"cartRemove") : undefined}
                        onClick={e => bump(e, room._id, qty - 1)}>
                        {qty === 1 ? <TrashIcon /> : "−"}
                      </button>
                      <span className="rc-qty-num">{qty}</span>
                      <button type="button" className="rc-qty-btn" disabled={qty >= cap}
                        onClick={e => bump(e, room._id, qty + 1)}>+</button>
                    </div>
                  ) : (
                    <button type="button" className="rc-add-btn"
                      disabled={full || availLoading || totalRooms >= MAX_GROUP_ROOMS}
                      title={full ? t(lang,"soldOut") : t(lang,"addToCart")}
                      aria-label={full ? t(lang,"soldOut") : t(lang,"addToCart")}
                      onClick={e => bump(e, room._id, 1)}>
                      <CartIcon />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          );
        })}
      </div>
    </div>

    {/* ── ปุ่มแท็บตะกร้าติดขอบขวาจอ — โผล่เฉพาะตอนมีของ ── */}
    {cartRows.length > 0 && (
      <button type="button" className="cart-fab" onClick={() => setShowCart(true)}
        aria-label={t(lang,"cartOpen")} title={t(lang,"cartOpen")}>
        <span className="cart-fab-icon">
          <CartIcon />
          <span className="cart-fab-badge">{totalRooms}</span>
        </span>
        <span className="cart-fab-total">฿{cartTotal.toLocaleString()}</span>
      </button>
    )}

    {/* ── Drawer ตะกร้า ──
        ไม่ผูกกับ cartRows.length — ลบชิ้นสุดท้ายขณะเปิดอยู่แล้ว drawer หายวับทั้งอันจะงง
        โชว์สถานะว่างแล้วให้ผู้ใช้กดปิดเองดีกว่า */}
    {showCart && (
      <>
        <div className="cart-overlay" onClick={() => setShowCart(false)} />
        <aside className="cart-drawer" role="dialog" aria-modal="true" aria-label={t(lang,"cartTitle")}>
          <div className="cart-drawer-head">
            <div>
              <div className="cart-panel-head">{t(lang,"cartTitle")}</div>
              <div className="cart-drawer-sub">
                {fmtDisplay(checkIn)} · {nights} {t(lang,"nightsUnit")}
              </div>
            </div>
            <button type="button" className="cart-drawer-close"
              onClick={() => setShowCart(false)}
              aria-label={t(lang,"cartClose")} title={t(lang,"cartClose")}>
              <CloseIcon />
            </button>
          </div>

          <div className="cart-drawer-body">
            {cartRows.length === 0
              ? <div className="cart-drawer-empty">{t(lang,"cartEmpty")}</div>
              : cartRows.map(({ room, qty }) => (
              <div className="cart-item" key={room._id}>
                <div className="cart-item-img">
                  {room.image
                    ? <img src={`/uploads/${room.image}`} alt={lang === "en" && room.nameEn ? room.nameEn : room.name} />
                    : <span>🛏️</span>}
                </div>
                <div className="cart-item-meta">
                  <div className="cart-item-name">{lang === "en" && room.nameEn ? room.nameEn : room.name}</div>
                  <div className="cart-item-price">
                    {room.price?.toLocaleString()} × {nights} {t(lang,"nightsUnit")}
                  </div>
                </div>
                {/* reuse .guest-counter — ปุ่มกลม ± หน้าตาตรงกับที่อื่นอยู่แล้ว */}
                <div className="guest-counter">
                  <button onClick={e => bump(e, room._id, qty - 1)}>−</button>
                  <span>{qty}</span>
                  <button disabled={qty >= capOf(room._id)}
                    onClick={e => bump(e, room._id, qty + 1)}>+</button>
                </div>
                <div className="cart-item-sub">
                  ฿{((room.price || 0) * qty * nights).toLocaleString()}
                </div>
                <button type="button" className="cart-item-del" title={t(lang,"cartRemove")}
                  onClick={() => removeItem(room._id)}>×</button>
              </div>
            ))}
          </div>

          <div className="cart-drawer-foot">
            <div className="cart-panel-total">
              <span>{t(lang,"cartTotal")}</span>
              <strong>฿{cartTotal.toLocaleString()}</strong>
            </div>
            <div className="cart-drawer-actions">
              <button type="button" className="cart-drawer-clear" disabled={!cartRows.length}
                onClick={() => clear()}>{t(lang,"cartClear")}</button>
              <button type="button" className="cart-drawer-go" disabled={!cartRows.length}
                onClick={goToCheckout}>{t(lang,"cartCheckout",totalRooms)}</button>
            </div>
          </div>
        </aside>
      </>
    )}

    <Toast show={!!toasting} type={toasting?.type} message={toasting?.msg} lang={lang} />
    </div>
  );
}

export default Rooms;